import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getPartTypes } from '../../services/partService';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  useTheme,
  IconButton,
  Link
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const SupplierMatrixReport = () => {
  const theme = useTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partTypes, setPartTypes] = useState([]);
  const [filters, setFilters] = useState({
    part_type_id: ''
  });

  // Fetch part types on component mount
  useEffect(() => {
    const fetchPartTypes = async () => {
      try {
        const partTypesData = await getPartTypes();
        setPartTypes(partTypesData);
      } catch (err) {
        console.error('Error fetching part types:', err);
        // Don't set error state here to avoid blocking the report
      }
    };

    fetchPartTypes();
  }, []);

  // Fetch report data when filters change
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query string from filters
        let queryString = '';
        if (filters.part_type_id) queryString += `part_type_id=${filters.part_type_id}&`;

        // Fetch report data
        const response = await axios.get(`/api/reports/supplier-matrix?${queryString}`, {
          headers: {
            'x-auth-token': localStorage.getItem('token')
          }
        });
        setReport(response.data);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching supplier matrix report:', err);
        setError('Failed to load supplier matrix report');
        setLoading(false);
      }
    };

    fetchReport();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');

      // Build query string from filters
      let queryString = '';
      if (filters.part_type_id) queryString += `part_type_id=${filters.part_type_id}&`;

      // Use axios to make a request with the token in the header
      axios({
        url: `/api/reports/export/supplier-matrix?${queryString}`,
        method: 'GET',
        responseType: 'blob', // Important for handling file downloads
        headers: {
          'x-auth-token': token
        }
      }).then((response) => {
        // Create a blob URL for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'supplier_matrix_report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch((error) => {
        console.error('Error downloading file:', error);
        toast.error('Failed to export report');
      });
    } catch (err) {
      console.error('Error exporting report:', err);
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatLeadTime = (days) => {
    if (!days) return '';
    return `${days} days`;
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            component={RouterLink}
            to="/reports"
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Supplier Matrix Report
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ ml: 2 }}
          >
            Export CSV
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Part Type</InputLabel>
                <Select
                  name="part_type_id"
                  value={filters.part_type_id}
                  onChange={handleFilterChange}
                  label="Part Type"
                >
                  <MenuItem value="">
                    <em>All Part Types</em>
                  </MenuItem>
                  {partTypes.map(partType => (
                    <MenuItem key={partType.id} value={partType.id}>
                      {partType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Statistics */}
        {report && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Parts
                  </Typography>
                  <Typography variant="h4">
                    {report.summary.total_parts}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Suppliers
                  </Typography>
                  <Typography variant="h4">
                    {report.summary.total_suppliers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Parts with Multiple Suppliers
                  </Typography>
                  <Typography variant="h4">
                    {report.summary.parts_with_multiple_suppliers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Parts with No Suppliers
                  </Typography>
                  <Typography variant="h4" color={report.summary.parts_with_no_suppliers > 0 ? 'error' : 'inherit'}>
                    {report.summary.parts_with_no_suppliers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Supplier Coverage Summary */}
        {report && report.summary.supplier_coverage && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Supplier Coverage
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(report.summary.supplier_coverage).map(([supplierName, coverage]) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={supplierName}>
                  <Card variant="outlined">
                    <CardContent sx={{ pb: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        {supplierName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {coverage.parts_count} parts ({coverage.coverage_percentage}%)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Matrix Table */}
        {report && report.matrix_data && (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Part Number
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Part Type
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Preferred Supplier
                    </TableCell>
                    {report.suppliers.map(supplier => (
                      <TableCell
                        key={supplier.id}
                        sx={{
                          minWidth: 150,
                          fontWeight: 'bold',
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center'
                        }}
                      >
                        {supplier.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.matrix_data.map((part) => (
                    <TableRow key={part.id} hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {part.internal_part_number}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={part.description}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {part.description}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {part.part_type?.name || ''}
                      </TableCell>
                      <TableCell>
                        {part.preferred_supplier ? (
                          <Chip
                            label={part.preferred_supplier.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : ''}
                      </TableCell>
                      {report.suppliers.map(supplier => {
                        const supplierPart = part.suppliers[supplier.id];
                        return (
                          <TableCell key={supplier.id} sx={{ textAlign: 'center' }}>
                            {supplierPart ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {supplierPart.supplier_part_number}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {formatCurrency(supplierPart.cost)}
                                </Typography>
                                {supplierPart.lead_time_days && (
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    {formatLeadTime(supplierPart.lead_time_days)}
                                  </Typography>
                                )}
                                {supplierPart.url && (
                                  <IconButton
                                    size="small"
                                    onClick={() => window.open(supplierPart.url, '_blank')}
                                    sx={{ mt: 0.5 }}
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* No Data Message */}
        {report && (!report.matrix_data || report.matrix_data.length === 0) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No parts found matching the selected filters.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default SupplierMatrixReport;
